/**
 * AdminPortal — the `/admin` route element (Phase 1 of App Shell Redesign).
 *
 * Previously a 700-line file that contained the Platform Settings form,
 * the two-axis tab/sidebar layout, the AccessDenied panel, and inline
 * mounts of every admin section. Phase 1 reduces this to a tiny guard:
 *
 *   - If the user is not an admin, render AccessDenied (preserves the
 *     existing chrome around the message for back-nav consistency).
 *   - Otherwise render `<AdminShell>` which holds the slate sidebar
 *     + topbar; `<Outlet>` resolves the child route into the right
 *     section page (Accounts, PooBucks, Tiers & plans, Fees, Settings,
 *     or one of the four stubs).
 *
 * All section logic moved to:
 *   - `components/admin/PlatformSettingsForm.jsx` (extracted form)
 *   - `pages/admin/AdminSectionPages.jsx`        (thin section wrappers)
 *   - `components/admin/AdminShell.jsx`          (sidebar + topbar chrome)
 *
 * The route tree is declared in `App.js`.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SharedNav from "@/components/SharedNav";
import SharedFooter from "@/components/SharedFooter";
import AvatarDropdown from "@/components/AvatarDropdown";
import AdminShell from "@/components/admin/AdminShell";
import { useAuth, pickPostLoginPath } from "@/context/AuthContext";
import { ShieldOff } from "lucide-react";

// ---------------------------------------------------------------------------
// AccessDenied — rendered when a non-admin hits /admin. Keeps the
// public marketing chrome around the panel so users have a path back.
// ---------------------------------------------------------------------------
function AccessDenied() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goHome = () => {
    if (!user) return navigate("/", { replace: true });
    navigate(pickPostLoginPath(user) || "/", { replace: true });
  };
  return (
    <div className="min-h-screen bg-background">
      <SharedNav rightSlot={<AvatarDropdown />} />
      <main className="mx-auto max-w-2xl px-6 py-24 text-center" data-testid="admin-access-denied">
        <ShieldOff className="mx-auto h-12 w-12 text-brand-orange" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-5xl font-bold text-pp-text sm:text-6xl">
          Access denied
        </h1>
        <p className="mt-3 text-base text-muted-foreground">
          This page is for PooPluckers admins only.
        </p>
        <Button
          onClick={goHome}
          className="mt-8 h-12 rounded-full bg-pp-orange px-8 text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid="admin-access-denied-back"
        >
          Back to dashboard
        </Button>
      </main>
      <SharedFooter />
    </div>
  );
}

export default function AdminPortal() {
  const { user } = useAuth();
  // ProtectedRoute(requireAdmin) renders this regardless of capability —
  // the gate lives here so the AccessDenied panel can carry the marketing
  // chrome around it.
  if (!user) return null;
  if (!user.is_admin) return <AccessDenied />;
  return <AdminShell />;
}
